import React, { useState, useEffect, useMemo } from 'react';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import stockpileService from '../../services/stockpileService';
import './StockpileVariantDetail.css';

// `value: 'all'` is a sentinel the parent translates into a backend
// `days=all` query param; the backend renders the variant's full history.
const FIXED_RANGE_OPTIONS = [
    { value: 7, label: '7d' },
    { value: 30, label: '30d' },
    { value: 90, label: '90d' },
    { value: 365, label: '1y' },
];
const ALL_OPTION = { value: 'all', label: 'All' };
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

const formatNumberFull = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('en-US', { maximumFractionDigits: 2 }).replace(/,/g, ' ');
};

const formatNumberShort = (value) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '0';
    const num = Math.abs(Number(value));
    const sign = Number(value) < 0 ? '-' : '';
    if (num >= 1_000_000) return `${sign}${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${sign}${(num / 1_000).toFixed(1)}k`;
    return `${sign}${num.toFixed(0)}`;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateShort = (iso) => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

const ChartTooltip = ({ active, payload, unit }) => {
    if (!active || !payload || !payload.length) return null;
    const point = payload[0].payload;
    return (
        <div className="stockpile-detail-tooltip">
            <div className="date">{formatDate(point.date)}</div>
            <div className="value">{formatNumberFull(point.balance)} {unit || ''}</div>
        </div>
    );
};

/**
 * Reusable per-variant stockpile detail block: range selector, area chart,
 * window stats. Caller controls `days` (or it manages its own state if not).
 *
 * Used by:
 *   - StockpileDetailModal (the big modal opened from the variant card)
 *   - StockpileDashboard's expanded row
 */
const StockpileVariantDetail = ({
    variantId,
    unitLabel,
    initialDays = 30,
    days: controlledDays,
    onDaysChange,
    chartHeight = 240,
    showRangePills = true,
    showStats = true,
    firstMovementAt = null,
}) => {
    const [internalDays, setInternalDays] = useState(initialDays);
    const days = controlledDays !== undefined ? controlledDays : internalDays;
    const setDays = (next) => {
        if (onDaysChange) onDaysChange(next);
        if (controlledDays === undefined) setInternalDays(next);
    };

    const [loading, setLoading] = useState(true);
    const [series, setSeries] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await stockpileService.getStockpile(variantId, {
                    limit: 1,
                    includeSeries: true,
                    days,
                });
                if (cancelled) return;
                setSeries(data.series || []);
            } catch (err) {
                if (cancelled) return;
                console.error('Stockpile detail series load failed', err);
                setError('Could not load chart.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [variantId, days]);

    const data = useMemo(
        () =>
            (series || []).map((p) => ({
                date: p.date,
                balance: Number(p.balance),
            })),
        [series]
    );

    const stats = useMemo(() => {
        if (!data.length) return null;
        const balances = data.map((d) => d.balance);
        return {
            min: Math.min(...balances),
            max: Math.max(...balances),
            latest: balances[balances.length - 1],
            first: balances[0],
            net: balances[balances.length - 1] - balances[0],
        };
    }, [data]);

    const gradientId = `stockpileDetailFill-${variantId}`;

    // Only surface "All" when there's history older than the largest fixed
    // pill; otherwise it duplicates the 1y view and just confuses people.
    const hasOlderThanOneYear = useMemo(() => {
        if (!firstMovementAt) return false;
        const first = new Date(firstMovementAt).getTime();
        if (Number.isNaN(first)) return false;
        return (Date.now() - first) > ONE_YEAR_MS;
    }, [firstMovementAt]);

    const rangeOptions = useMemo(
        () => (hasOlderThanOneYear ? [...FIXED_RANGE_OPTIONS, ALL_OPTION] : FIXED_RANGE_OPTIONS),
        [hasOlderThanOneYear]
    );

    const isAll = days === 'all';
    const netLabel = isAll ? 'Net change (all)' : `Net change (${days}d)`;
    const loadingLabel = isAll ? 'Loading full history…' : `Loading ${days}-day series…`;

    return (
        <div className="stockpile-detail">
            {showRangePills && (
                <div className="stockpile-detail-controls">
                    <span className="label">Range</span>
                    {rangeOptions.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            className={`stockpile-detail-pill ${days === opt.value ? 'active' : ''}`}
                            onClick={() => setDays(opt.value)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="stockpile-detail-chart" style={{ height: chartHeight }}>
                {loading ? (
                    <div className="stockpile-detail-loading">{loadingLabel}</div>
                ) : error ? (
                    <div className="stockpile-detail-loading">{error}</div>
                ) : data.length === 0 ? (
                    <div className="stockpile-detail-empty">No movements in this window.</div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data} margin={{ top: 12, right: 18, bottom: 6, left: 0 }}>
                            <defs>
                                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#2563eb" stopOpacity={0.4} />
                                    <stop offset="100%" stopColor="#2563eb" stopOpacity={0.04} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="date"
                                stroke="#94a3b8"
                                tickFormatter={formatDateShort}
                                tick={{ fontSize: 11 }}
                                minTickGap={24}
                            />
                            <YAxis
                                stroke="#94a3b8"
                                tickFormatter={formatNumberShort}
                                tick={{ fontSize: 11 }}
                                width={64}
                            />
                            <Tooltip
                                content={<ChartTooltip unit={unitLabel} />}
                                cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }}
                            />
                            <Area
                                type="monotone"
                                dataKey="balance"
                                stroke="#1d4ed8"
                                strokeWidth={2}
                                fill={`url(#${gradientId})`}
                                isAnimationActive={false}
                                activeDot={{ r: 4, fill: '#1d4ed8' }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            {showStats && stats && (
                <div className="stockpile-detail-stats">
                    <div className="stat">
                        <span className="label">Min</span>
                        <span className="value">{formatNumberFull(stats.min)} {unitLabel}</span>
                    </div>
                    <div className="stat">
                        <span className="label">Max</span>
                        <span className="value">{formatNumberFull(stats.max)} {unitLabel}</span>
                    </div>
                    <div className="stat">
                        <span className="label">Latest</span>
                        <span className="value">{formatNumberFull(stats.latest)} {unitLabel}</span>
                    </div>
                    <div className="stat">
                        <span className="label">{netLabel}</span>
                        <span className={`value ${stats.net >= 0 ? 'positive' : 'negative'}`}>
                            {stats.net >= 0 ? '+' : ''}{formatNumberFull(stats.net)} {unitLabel}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockpileVariantDetail;
